import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { FiX, FiUser, FiMail, FiLock, FiSettings } from 'react-icons/fi';

const SubUserModal = ({ subUser, projects, permissionTemplates, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    accessibleProjects: []
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  const isEditing = !!subUser;

  useEffect(() => {
    if (subUser) {
      // Normalize accessibleProjects to handle both populated and non-populated data
      const normalizedAccessibleProjects = (subUser.accessibleProjects || []).map(ap => ({
        project: typeof ap.project === 'object' ? ap.project._id : ap.project,
        permissions: ap.permissions || {}
      }));

      console.log('SubUserModal: Original accessibleProjects:', subUser.accessibleProjects);
      console.log('SubUserModal: Normalized accessibleProjects:', normalizedAccessibleProjects);

      setFormData({
        name: subUser.name || '',
        email: subUser.email || '',
        accessibleProjects: normalizedAccessibleProjects
      });
    }
  }, [subUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleProjectToggle = (projectId) => {
    setFormData(prev => {
      const existingIndex = prev.accessibleProjects.findIndex(ap => ap.project === projectId);
      
      if (existingIndex >= 0) {
        // Remove project
        return {
          ...prev,
          accessibleProjects: prev.accessibleProjects.filter(ap => ap.project !== projectId)
        };
      } else {
        // Add project with default permissions
        return {
          ...prev,
          accessibleProjects: [
            ...prev.accessibleProjects,
            {
              project: projectId,
              permissions: {
                viewDashboard: true,
                viewAnalytics: false,
                editProject: false,
                viewApiEndpoints: false,
                manageApiEndpoints: false,
                viewKnowledgeBase: false,
                manageKnowledgeBase: false,
                viewFlowBuilder: false,
                manageFlowBuilder: false,
                viewHumanAgents: false,
                manageHumanAgents: false,
                viewLocations: false,
                manageLocations: false,
                viewSettings: false,
                manageSettings: false,
                viewSDKIntegration: false,
                testChatbot: true
              }
            }
          ]
        };
      }
    });
  };

  const handlePermissionChange = (projectId, permission, value) => {
    setFormData(prev => ({
      ...prev,
      accessibleProjects: prev.accessibleProjects.map(ap => 
        ap.project === projectId
          ? {
              ...ap,
              permissions: {
                ...ap.permissions,
                [permission]: value
              }
            }
          : ap
      )
    }));
  };

  const applyPermissionTemplate = (projectId, templateKey) => {
    const template = permissionTemplates?.[templateKey];
    if (!template) return;

    setFormData(prev => ({
      ...prev,
      accessibleProjects: prev.accessibleProjects.map(ap => 
        ap.project === projectId
          ? {
              ...ap,
              permissions: { ...template.permissions }
            }
          : ap
      )
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.accessibleProjects.length === 0) {
      newErrors.projects = 'Please select at least one project';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving sub user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const permissionLabels = {
    viewDashboard: 'View Dashboard',
    viewAnalytics: 'View Analytics',
    editProject: 'Edit Project Settings',
    viewApiEndpoints: 'View API Endpoints',
    manageApiEndpoints: 'Manage API Endpoints',
    viewKnowledgeBase: 'View Knowledge Base',
    manageKnowledgeBase: 'Manage Knowledge Base',
    viewFlowBuilder: 'View Flow Builder',
    manageFlowBuilder: 'Manage Flow Builder',
    viewHumanAgents: 'View Human Agents',
    manageHumanAgents: 'Manage Human Agents',
    viewLocations: 'View Locations',
    manageLocations: 'Manage Locations',
    viewSettings: 'View Settings',
    manageSettings: 'Manage Settings',
    viewSDKIntegration: 'View SDK Integration',
    testChatbot: 'Test Chatbot'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            {isEditing ? 'Edit Sub User' : 'Create New Sub User'}
          </h2>
          <Button
            onClick={onClose}
            variant="outline"
            size="sm"
            className="text-gray-500 hover:text-gray-700"
          >
            <FiX className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          {/* Tabs */}
          <div className="border-b">
            <div className="flex px-6">
              <button
                type="button"
                onClick={() => setActiveTab('basic')}
                className={`py-3 px-4 text-sm font-medium border-b-2 ${
                  activeTab === 'basic'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <FiUser className="inline-block w-4 h-4 mr-2" />
                Basic Info
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('permissions')}
                className={`py-3 px-4 text-sm font-medium border-b-2 ${
                  activeTab === 'permissions'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <FiSettings className="inline-block w-4 h-4 mr-2" />
                Projects & Permissions
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'basic' && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={errors.name ? 'border-red-500' : ''}
                    placeholder="Enter full name"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? 'border-red-500' : ''}
                    placeholder="Enter email address"
                    disabled={isEditing} // Don't allow email changes when editing
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">What happens after creating this sub user?</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• An invitation email will be sent to the user</li>
                    <li>• They'll click the verification link and create their own password</li>
                    <li>• Once verified, they can log in with their email and password</li>
                    <li>• They'll only see projects and features you give them access to</li>
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'permissions' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Project Access</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Select which projects this user can access and configure their permissions for each project.
                  </p>
                  {errors.projects && <p className="text-red-500 text-sm mb-4">{errors.projects}</p>}
                </div>

                {!Array.isArray(projects) || projects.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No projects available. Create a project first to assign access.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Array.isArray(projects) && projects.map((project) => {
                      const hasAccess = formData.accessibleProjects.some(ap => ap.project === project._id);
                      const projectPermissions = formData.accessibleProjects.find(ap => ap.project === project._id)?.permissions || {};

                      return (
                        <div key={project._id} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id={`project-${project._id}`}
                                checked={hasAccess}
                                onChange={() => handleProjectToggle(project._id)}
                                className="h-4 w-4 text-blue-600 rounded border-gray-300"
                              />
                              <label htmlFor={`project-${project._id}`} className="ml-3">
                                <div className="text-sm font-medium text-gray-900">{project.name}</div>
                                {project.clientName && (
                                  <div className="text-sm text-gray-500">Client: {project.clientName}</div>
                                )}
                              </label>
                            </div>

                            {hasAccess && (
                              <div className="flex space-x-2">
                                <select
                                  value=""
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      applyPermissionTemplate(project._id, e.target.value);
                                      e.target.value = '';
                                    }
                                  }}
                                  className="text-sm border border-gray-300 rounded px-2 py-1"
                                >
                                  <option value="">Apply template...</option>
                                  {Object.entries(permissionTemplates || {}).map(([key, template]) => (
                                    <option key={key} value={key}>
                                      {template.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>

                          {hasAccess && (
                            <div className="ml-7 border-t pt-4">
                              <h4 className="text-sm font-medium text-gray-700 mb-3">Permissions</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {Object.entries(permissionLabels).map(([permission, label]) => (
                                  <label key={permission} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={projectPermissions[permission] || false}
                                      onChange={(e) => handlePermissionChange(project._id, permission, e.target.checked)}
                                      className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">{label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border-t p-6">
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  isEditing ? 'Update Sub User' : 'Create Sub User'
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubUserModal; 